import React, { useEffect, useMemo, useState } from "react";
import { ReactComponent as Logo } from "../assets/ZEDIPASS-LOGO-LOCKUP.svg";
import { Link } from "react-router-dom";
import { openConsentSettings } from "../utils/consent";
import { BsInstagram, BsLinkedin, BsTablet, BsTiktok, BsX } from "react-icons/bs";
import {
  SiVisa,
  SiMastercard,
  SiAmericanexpress,
  SiApplepay,
  SiGooglepay,
  SiStripe
} from "react-icons/si";
import { AnimatePresence, motion } from "framer-motion";
import AppDownloadBadges from "./AppDownloadBadges";
import { resolveDashboardUrl } from "../utils/dashboardUrl";

const socialLinks = [
  {
    label: "LinkedIn",
    href: "https://uk.linkedin.com/company/zedipass",
    Icon: BsLinkedin,
    hover: "hover:border-sky-400/60 hover:bg-sky-500/10"
  },
  {
    label: "Instagram",
    href: "https://www.instagram.com/zedipass/",
    Icon: BsInstagram,
    hover: "hover:border-pink-400/60 hover:bg-pink-500/10"
  },
  {
    label: "TikTok",
    href: "https://www.tiktok.com/@zedipass",
    Icon: BsTiktok,
    hover: "hover:border-cyan-300/60 hover:bg-cyan-400/10"
  }
];

const Footer = () => {
  const [showQrModal, setShowQrModal] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const dashboardUrl = resolveDashboardUrl();

  const navSections = useMemo(
    () => [
      {
        title: "Product",
        links: [
          { label: "Homepage", to: "/" },
          { label: "Overview", to: "/product/" },
          { label: "White-label & Referral", to: "/white-label-referral" },
          { label: "Pricing", to: "/pricing" },
          { label: "FAQ", to: "/faq" }
        ]
      },
      {
        title: "Company",
        links: [
          { label: "Our Story", to: "/our-story" },
          { label: "Pilot Programme", to: "/pilot" },
          { label: "Register your interest", to: "/register" },
          { label: "Partners", to: "/partners" },
          { label: "Investor Relations", to: "/partners/investors" }
        ]
      },
      {
        title: "Resources",
        links: [
          { label: "Resource Centre", to: "/resources" },
          
          { label: "Find your fit", to: "/findyourfit" },
          // { label: "Solutions", to: "/solutions?tab=accessibility" },
          ...(dashboardUrl ? [{ label: "Dashboard", href: dashboardUrl }] : []),
          { label: "F&F Hub", to: "/friends-family" }
        ]
      }
    ],
    [dashboardUrl]
  );


  useEffect(() => {
    const checkViewport = () => {
      setIsMobile(window.matchMedia("(max-width: 640px)").matches);
    };

    checkViewport();
    window.addEventListener("resize", checkViewport);
    return () => window.removeEventListener("resize", checkViewport);
  }, []);

  return (
    <footer className="relative overflow-hidden bg-gray-950 text-gray-200">
      <div
        className="pointer-events-none absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-950 to-black"
        aria-hidden
      />
      <div className="absolute -top-24 right-0 h-64 w-64 rounded-full bg-indigo-500/20 blur-3xl" aria-hidden />
      <div className="relative z-10">
        <div className="mx-auto max-w-7xl px-6 py-8 sm:px-10 lg:px-12">
          <div className="grid gap-8 lg:grid-cols-[1.1fr,1fr]">
            <div className="space-y-6">
              <div className="space-y-6">
                <Logo className="h-8 w-auto text-white" />
                <p className="max-w-xl text-sm leading-relaxed text-gray-300">
                  ZediPass helps independent businesses turn first visits into lasting customer relationships.
                </p>
                <div className="space-y-3">
                  <p className="text-[0.72rem] font-semibold uppercase tracking-[0.2em] text-gray-500">
                    Follow ZediPass
                  </p>
                  <ul className="flex flex-wrap items-center gap-3" aria-label="ZediPass social media">
                    {socialLinks.map(({ label, href, Icon, hover }) => (
                      <li key={label}>
                        <a
                          href={href}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={`ZediPass on ${label} (opens in a new tab)`}
                          title={label}
                          className={`group inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-white/5 text-gray-200 transition duration-200 hover:-translate-y-0.5 hover:text-white motion-reduce:transition-none motion-reduce:hover:translate-y-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-950 ${hover}`}
                        >
                          <Icon className="h-5 w-5" aria-hidden="true" />
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="space-y-3">
                <AppDownloadBadges placement="global_footer" />
              </div>

            </div>

            <div className="grid gap-6">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {navSections.map(({ title, links }) => (
                  <div key={title} className="space-y-3">
                    <h3 className="hidden text-sm font-semibold uppercase tracking-widest text-gray-400 sm:block">
                      {title}
                    </h3>
                    <details className="sm:hidden">
                      <summary className="flex cursor-pointer items-center justify-between rounded-lg bg-white/5 px-4 py-3 text-sm font-semibold text-white">
                        {title}
                        <span aria-hidden>+</span>
                      </summary>
                      <ul className="mt-3 space-y-3 px-4 pb-3 text-sm">
                        {links.map(({ label, to, href }) => (
                          <li key={label}>
                            {to ? (
                              <Link to={to} className="block text-blue-400 transition hover:text-blue-300">
                                {label}
                              </Link>
                            ) : (
                              <a
                                href={href}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block text-blue-400 transition hover:text-blue-300"
                              >
                                {label}
                              </a>
                            )}
                          </li>
                        ))}
                      </ul>
                    </details>
                    <ul className="hidden space-y-2 text-sm sm:block">
                      {links.map(({ label, to, href }) => (
                        <li key={label}>
                          {to ? (
                            <Link to={to} className="text-blue-400 transition hover:text-blue-300">
                              {label}
                            </Link>
                          ) : (
                            <a
                              href={href}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-400 transition hover:text-blue-300"
                            >
                              {label}
                            </a>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

            </div>
          </div>

          <div className="mt-8 border-t border-white/10 pt-5">
            <div className="flex flex-col gap-6">
              <nav
                aria-label="Footer legal links"
                className="flex flex-wrap items-center gap-x-3 gap-y-2 text-[0.72rem] font-semibold uppercase tracking-[0.2em] text-gray-300 sm:text-[0.78rem]"
              >
                <Link to="/privacy" className="transition hover:text-white">
                  Privacy
                </Link>
                <span aria-hidden className="text-gray-600">·</span>
                <Link to="/terms" className="transition hover:text-white">
                  Terms
                </Link>
                <span aria-hidden className="text-gray-600">·</span>
                <Link to="/cookies" className="transition hover:text-white">
                  Cookies
                </Link>
                <span aria-hidden className="text-gray-600">·</span>
                <button type="button" onClick={openConsentSettings} className="transition hover:text-white">
                  Cookie settings
                </button>
                <span aria-hidden className="text-gray-600">·</span>
                <Link to="/gdpr" className="transition hover:text-white">
                  GDPR
                </Link>
                <span aria-hidden className="text-gray-600">·</span>
                <Link to="/delete-account" className="transition hover:text-white">
                  Delete Account
                </Link>
              </nav>

              <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
                <div className="space-y-4">
                  <div className="flex flex-wrap items-center gap-3 text-xs text-gray-300 sm:text-sm">
                    <span className="text-[0.72rem] font-semibold uppercase tracking-[0.2em] text-gray-500">
                      Business subscription billing
                    </span>
                    <div className="flex items-center gap-3 rounded-full border border-white/10 bg-black/20 px-3 py-2">
                      <SiVisa aria-label="Visa" className="h-4 w-4" />
                      <SiMastercard aria-label="Mastercard" className="h-4 w-4" />
                      <SiAmericanexpress aria-label="American Express" className="h-4 w-4" />
                      <SiApplepay aria-label="Apple Pay" className="h-4 w-4" />
                      <SiGooglepay aria-label="Google Pay" className="h-4 w-4" />
                    </div>
                    <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-2 text-xs font-medium text-gray-200">
                      <SiStripe aria-hidden="true" className="h-4 w-4" />
                      Powered by Stripe
                    </span>
                  </div>

                  <div className="space-y-1.5 text-xs text-gray-400 sm:text-sm">
                    <p className="font-medium text-gray-200">&copy; {new Date().getFullYear()} ZEDIPASS LIMITED. All rights reserved.</p>
                    <p>ZEDIPASS LIMITED · Company No. 16508367 · UK registered.</p>
                    <p>Registered with the UK Information Commissioner’s Office (ICO) for data protection.</p>
                  </div>
                </div>

                {!isMobile && (
                  <button
                    onClick={() => setShowQrModal(true)}
                    className="inline-flex items-center gap-2 self-start rounded-lg border border-white/10 bg-white/5 px-4 py-2 text-sm font-semibold text-white transition hover:border-indigo-400 hover:bg-indigo-500/10 lg:self-auto"
                  >
                    <BsTablet className="h-4 w-4" />
                    Show QR
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {showQrModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowQrModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="relative w-full max-w-sm rounded-2xl bg-white p-8 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setShowQrModal(false)}
                className="absolute right-4 top-4 text-gray-500 transition hover:text-gray-700"
              >
                <BsX size={24} className="text-neutral-900 dark:text-white" />
              </button>

              <div className="text-center">
                <Logo className="mx-auto mb-3 h-6 w-auto text-gray-900" />
                <p className="mb-6 text-sm text-gray-600">Scan to open ZEDIPASS on mobile</p>

                <div className="inline-block rounded-xl bg-white p-4 shadow-inner">
                  <img
                    src="/zedipass-download-qr.png"
                    alt="QR code to download ZediPass from the correct app store"
                    className="mx-auto h-[180px] w-[180px]"
                  />
                  <p className="mt-2 break-all text-xs text-gray-500">
                    www.zedipass.com/get
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </footer>
  );
};

export default Footer;
